import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Tabs, Tab, useToast } from "barte-design-system";
import { usePostHog } from '@posthog/react';

import { HeaderPage } from "@/modules/shared/components";
import { TRACK_EVENTS } from "@payfac/constants";
import {
    useGetRatePlansDetails,
    usePartialUpdateRatePlan,
    useUpdateRatePlan,
} from "@payfac/services";

import { RatePlanDetails, RatePlanSellers, RatePlanHistory } from "../components";

import type { IRatePlanDetails } from "@payfac/types";
import styles from "./show.module.css";

type RatePlanShowTab = "details" | "sellers" | "history";

export const RatePlanShow = () => {
    const { t } = useTranslation();
    const toast = useToast();
    const { id } = useParams<{ id: string }>();
    const posthog = usePostHog();

    const [activeTab, setActiveTab] = useState<RatePlanShowTab>("details");

    const { data: response, isLoading } = useGetRatePlansDetails({
        planId: +(id) || 0,
    });
    const { mutateAsync: updateRatePlanTitle, isPending: isPendingPatch } =
        usePartialUpdateRatePlan();

    const data = response?.data;

    const { mutateAsync: updateRatePlan, isPending } = useUpdateRatePlan();

    if (!data && !isLoading) {
        return <div>{t("payfac.pageRatePlanShowNotFound")}</div>;
    }

    const onSubmitData = async (
        payload: IRatePlanDetails,
        onSuccess: () => void,
    ) => {
        posthog.capture(TRACK_EVENTS.RATE_PLAN.UPDATED_INTENT);

        delete payload?.id;
        delete payload?.tax_aggregate_version;

        const res = await updateRatePlan({ planId: id, payload });

        if (res.success) {
            posthog.capture(TRACK_EVENTS.RATE_PLAN.UPDATED_SUCCESS);

            toast.success({
                title: res?.message,
            });

            onSuccess?.();
        } else {
            posthog.capture(TRACK_EVENTS.RATE_PLAN.UPDATED_ERROR, {
                error: res?.data?.errors?.[0]?.title || res?.message
            });

            toast.error({
                title: res?.data?.errors?.[0]?.title,
                description: res?.message,
            });
        }
    };

    const tabs = {
        details: {
            label: t("payfac.pageRatePlanShowDetailsTab"),
            content: (
                <RatePlanDetails
                    data={data}
                    isLoading={isLoading}
                    onSubmitData={onSubmitData}
                    isPending={isPending}
                />
            ),
        },
        sellers: {
            label: t("payfac.pageRatePlanShowSellersTab"),
            content: <RatePlanSellers planId={id!} />,
        },
        history: {
            label: t("payfac.pageRatePlanShowHistoryTab"),
            content: id ? <RatePlanHistory planId={id} /> : null,
        },
    };

    const onTitleChange = async (newTitle: string) => {
        posthog.capture(TRACK_EVENTS.RATE_PLAN.UPDATED_INTENT, { action: 'title_change' });

        if (!newTitle.trim()) {
            posthog.capture(TRACK_EVENTS.RATE_PLAN.UPDATED_ERROR, {
                error: t('payfac.pageRatePlanFormNameRequired')
            });

            return;
        }

        const payload = {
            ...data,
            name: newTitle,
            title: newTitle,
            description: newTitle,
        };

        const res = await updateRatePlanTitle({ planId: id, payload });

        if (res.success) {
            posthog.capture(TRACK_EVENTS.RATE_PLAN.UPDATED_SUCCESS);

            toast.success({
                title: res?.message,
            });
        } else {
            posthog.capture(TRACK_EVENTS.RATE_PLAN.UPDATED_ERROR, {
                error: res?.data?.errors?.[0]?.title || res?.message
            });

            toast.error({
                title: res?.data?.errors?.[0]?.title,
                description: res?.message,
            });
        }
    };

    const onClickTab = (tab: RatePlanShowTab) => {
        setActiveTab(tab);

        posthog.capture(TRACK_EVENTS.RATE_PLAN.TAB_CHANGED, { tab });
    };

    useEffect(() => {
        if (id) {
            posthog.capture(TRACK_EVENTS.RATE_PLAN.SHOW_VIEWED, { ratePlanId: id });
        }
    }, [id]);

    return (
        <div className={styles.container}>
            <Helmet>
                <title>{data?.name ? `${data.name} - Barte` : "Barte"}</title>
            </Helmet>

            <HeaderPage
                title={data?.name}
                isLoading={isLoading || isPendingPatch}
                withBackButton
                onTitleChange={onTitleChange}
            />

            <div className={styles.content}>
                <Tabs>
                    {Object.entries(tabs).map(([key, value]) => (
                        <Tab
                            key={key}
                            active={activeTab === key}
                            onClick={() => onClickTab(key as RatePlanShowTab)}
                        >
                            {value.label}
                        </Tab>
                    ))}
                </Tabs>

                {tabs[activeTab]?.content}
            </div>
        </div>
    );
};
